import type { Agent } from '@studio/models/agent';
import type { Device } from '@studio/models/device';

/**
 * Device teardown registry (ENG-4477).
 *
 * Deleting a source while its image/control settings are still loading left
 * several async flows holding the device id across `await` boundaries. When
 * they resumed after the delete they fired orphaned state-sync mutations at
 * the torn-down device; each one timed out at the NATS layer, forced a
 * `synchronize('resync')`, and the overlapping resyncs raced each other
 * ("A second state-sync run started before the previous synchronization
 * completed"), driving `bridge_goes_offline` plus a discovery-toast storm.
 *
 * This module is the shared source of truth those flows consult:
 *
 * - a bounded tombstone set of deleted device ids, so any settings flow that
 *   resolves after the delete can bail out instead of mutating; and
 * - per-agent pending-teardown flights, so a retry path (e.g. an immediate
 *   re-discovery) can wait for the teardown to settle instead of racing it.
 *
 * Kept dependency-free (types only) so leaf modules (settings mutation
 * handlers, watchdogs, discovery) can import it without dragging in the
 * command implementations.
 */

/**
 * Tombstones of deleted device ids. Guards consult this to refuse firing a
 * mutation at a device that has been deleted.
 *
 * Bounded like `cleanedUpDevices` in agent/device-cleanup.ts — per rule:
 * investigate-before-adding (ai.knowledge/global), this follows the existing
 * bounded-tracking-set convention so the registry can never become a leak.
 */
const deletedDevices = new Set<Device['id']>();

/** Maximum tombstones tracked before pruning the oldest half. */
const MAX_DELETED_TRACKING_SIZE = 1000;

/**
 * Pending teardown flights per agent. Each device removal registers a promise
 * for the duration of the teardown; retry paths await these before mutating
 * the same agent again.
 */
const pendingTeardownsByAgent = new Map<Agent['id'], Set<Promise<void>>>();

/**
 * Marks a device as deleted and registers a pending teardown flight on its
 * agent. Returns a settle function the teardown MUST call on every terminal
 * path (per rule: settle-every-terminal-branch, ai.knowledge/global) so
 * waiters are released even when the removal mutation fails.
 *
 * @param deviceId - The device being deleted.
 * @param agentId - The agent that owns the device (omit when unknown; the
 *   tombstone is still recorded so mutation guards work regardless).
 * @returns A settle function that resolves this teardown flight.
 */
export const beginDeviceTeardown = (
  deviceId: Device['id'],
  agentId?: Agent['id']
): (() => void) => {
  deletedDevices.add(deviceId);

  // Prune the oldest half when the tombstone set outgrows its bound —
  // insertion order is iteration order for Set, so the front is the oldest.
  if (deletedDevices.size > MAX_DELETED_TRACKING_SIZE) {
    const iterator = deletedDevices.values();
    for (let i = 0; i < MAX_DELETED_TRACKING_SIZE / 2; i++) {
      const value = iterator.next().value;
      if (value !== undefined) {
        deletedDevices.delete(value);
      }
    }
  }

  if (!agentId) {
    return () => {};
  }

  let settle!: () => void;
  const flight = new Promise<void>((resolve) => {
    settle = resolve;
  });

  let flights = pendingTeardownsByAgent.get(agentId);

  if (!flights) {
    flights = new Set();
    pendingTeardownsByAgent.set(agentId, flights);
  }

  flights.add(flight);

  return () => {
    settle();
    flights.delete(flight);

    if (flights.size === 0) {
      pendingTeardownsByAgent.delete(agentId);
    }
  };
};

/**
 * Whether the given device has been deleted (teardown started). Settings
 * flows that resumed from an `await` MUST consult this before mutating —
 * a mutation aimed at a deleted device can only race state sync.
 *
 * @param deviceId - The device to check.
 * @returns `true` when the device was deleted this session.
 */
export const isDeviceDeleted = (deviceId: Device['id']): boolean => deletedDevices.has(deviceId);

/**
 * Clears a device's tombstone. Called when a device id is (re)added so a
 * stale tombstone can never block a legitimately re-added device.
 *
 * @param deviceId - The device being added.
 */
export const clearDeviceDeleted = (deviceId: Device['id']): void => {
  deletedDevices.delete(deviceId);
};

/** The control maps a removal mutation clears (see remove.ts). */
type DeviceMaps = {
  readonly device?: Readonly<Record<string, unknown>> | undefined;
  readonly studio?: { readonly device?: Readonly<Record<string, unknown>> | undefined } | undefined;
};

/**
 * Returns `control` without tombstoned devices in `device` and `studio.device`
 * (the maps the removal mutation clears). Snapshot saves run this so a device
 * that lingers in agent state after its delete is never persisted, whichever
 * save (the delete's own, or a later add's) writes next. Returns the same
 * object when nothing is tombstoned, so unaffected saves are unchanged.
 *
 * @param control - Control state about to be persisted.
 * @returns Control state with deleted devices omitted.
 */
export const omitDeletedDevices = <T extends DeviceMaps>(control: T): T => {
  const isDeleted = (id: string): boolean => deletedDevices.has(id as Device['id']);
  const device = control.device ?? {};
  const studioDevice = control.studio?.device ?? {};
  if (!Object.keys(device).some(isDeleted) && !Object.keys(studioDevice).some(isDeleted)) {
    return control;
  }
  const keep = (map: Readonly<Record<string, unknown>>) =>
    Object.fromEntries(Object.entries(map).filter(([id]) => !isDeleted(id)));
  // Shallow copies only along the filtered paths; live state is never mutated.
  return {
    ...control,
    ...(control.device ? { device: keep(control.device) } : {}),
    ...(control.studio?.device
      ? { studio: { ...control.studio, device: keep(control.studio.device) } }
      : {})
  } as T;
};

/**
 * Whether the agent has a device teardown still in flight.
 *
 * @param agentId - The agent to check.
 * @returns `true` when at least one teardown flight is pending.
 */
export const hasPendingTeardown = (agentId: Agent['id']): boolean =>
  pendingTeardownsByAgent.has(agentId);

/**
 * Waits for the agent's in-flight device teardowns to settle, bounded by
 * `capMs`. This is an intelligent wait on a real condition (per rule:
 * e2e-tests-fail-fast-pass-fast, ai.knowledge/global — never a fixed sleep):
 * it resolves the moment the teardown settles, and the cap only exists so a
 * pathologically wedged teardown cannot block the caller forever.
 *
 * @param agentId - The agent whose teardowns to await.
 * @param capMs - Upper bound on the wait.
 */
export const awaitPendingTeardowns = async (
  agentId: Agent['id'],
  capMs: number = 10_000
): Promise<void> => {
  const flights = pendingTeardownsByAgent.get(agentId);

  if (!flights || flights.size === 0) {
    return;
  }

  const pending = Promise.all(flights).then(() => undefined);

  let capTimer: ReturnType<typeof setTimeout> | undefined;

  const cap = new Promise<void>((resolve) => {
    capTimer = setTimeout(resolve, capMs);
  });

  try {
    await Promise.race([pending, cap]);
  } finally {
    // Always clear the cap timer so a settled teardown does not leave a live
    // timer behind (and so jest fake-timer tests do not leak handles).
    if (capTimer !== undefined) {
      clearTimeout(capTimer);
    }
  }
};

/**
 * Resets all teardown tracking. Test-only escape hatch; production code never
 * needs to reset the registry wholesale.
 */
export const resetDeviceTeardownTracking = (): void => {
  deletedDevices.clear();
  pendingTeardownsByAgent.clear();
};
